import React, { useEffect, useRef, useState } from "react"
import * as Yup from "yup"
import { useQueryClient } from "react-query"
import { Form, Formik, FormikHelpers } from "formik"
import { toast } from "react-toastify"
import axios, { AxiosError } from "axios"
import Swal from "sweetalert2"
import withReactContent from "sweetalert2-react-content"

// Models & Constants
import { IConsultantNdfDetailDocumentModel, IConsultantNdfDetailModel, IHttpErrorResponseModel } from "@common-models/*"
import { INdfDetailEditDepenseGroupedRequestModel } from "../../../../../core/_models"
import {
     API_URL_ESPACE_AVENTURIER,
     CONST_API_UTILS_GET_GOOGLE_MAPS_DISTANCE_BETWEEN_TWO_POINTS_IN_KM_ENDPOINT,
     CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR,
     CONST_NDF_DETAILS_CATEGORIE_KM,
     CONST_NDF_DETAILS_CATEGORIE_REPAS_MIDI,
} from "../../../../../../../../constants"

// Utils
import MyAlert from "../../../../../../../../utils/MyAlert"
import MySimpleSpinner from "../../../../../../../../utils/MySimpleSpinner"
import FileAndDriveHelper from "../../../../../../../../helpers/FileAndDriveHelper"

// Requests
import { ndfDetailAddDocument, ndfDetailDocumentDelete, ndfDetailDocumentReplace, ndfDetailEditDepenseGrouped } from "../../../../../core/_request"

// Context & Subcomponents
import { useNdfDetailsSalarieContext } from "../../../NdfDetails/salarie/NdfDetailsSalarie"
import { CONST_MONTANT_INDEMNITE_PAR_KM } from "@common-constants/*"
import { NdfGroupedDetailsEditSection } from "./components/NdfGroupedDetailsEditSection"
import { NdfJustificatifsEditSection } from "./components/NdfJustificatifsEditSection"

interface INdfUpdateDetailsEditDepenseGroupedPropsModel {
     detail: IConsultantNdfDetailModel
}

const NdfEditDepenseGrouped = ({ detail }: INdfUpdateDetailsEditDepenseGroupedPropsModel) => {
     const queryClient = useQueryClient()
     const context = useNdfDetailsSalarieContext()
     const MySwal = withReactContent(Swal)

     // --- ÉTATS POUR LE LIVE EDIT DES DOCUMENTS ---
     const [localDocuments, setLocalDocuments] = useState<IConsultantNdfDetailDocumentModel[]>((detail.consultantNdfDocuments as any) || [])
     const [isLoadingDocAction, setIsLoadingDocAction] = useState(false)
     const [docToBeReplaced, setDocToBeReplaced] = useState<IConsultantNdfDetailDocumentModel>()
     const refReplaceDocInput = useRef<HTMLInputElement>(null)
     const refAddDocInput = useRef<HTMLInputElement>(null)

     const [detailsIndemniteKm, setDetailsIndemniteKm] = useState<Record<number, any>>({})

     const validationSchema = Yup.object().shape({
          depenses: Yup.array().of(
               Yup.object().shape({
                    categorie: Yup.string().label("Catégorie").required("Champ requis").nullable(),
                    numeroTicket: Yup.number()
                         .label("Numéro de ticket")
                         .typeError("Format incorrect.")
                         .when("categorie", {
                              is: (val: string) => val !== CONST_NDF_DETAILS_CATEGORIE_KM,
                              then: s => s.required("Champ requis"),
                              otherwise: s => s.notRequired().nullable(),
                         }),
                    date: Yup.string()
                         .label("Date")
                         .when("categorie", {
                              is: (val: string) => val !== CONST_NDF_DETAILS_CATEGORIE_KM,
                              then: s => s.required("Champ requis"),
                              otherwise: s => s.notRequired().nullable(),
                         }),
                    montantTTC: Yup.number()
                         .label("Montant TTC")
                         .when("categorie", {
                              is: (val: string) => val !== CONST_NDF_DETAILS_CATEGORIE_KM,
                              then: s => s.required("Champ requis"),
                         })
                         .nullable(),
                    description: Yup.string()
                         .label("Commentaire")
                         .nullable()
                         .when("categorie", {
                              is: (val: string) => val !== CONST_NDF_DETAILS_CATEGORIE_KM,
                              then: s => s.required("Champ requis"),
                              otherwise: s => s.notRequired(),
                         }),
                    // CHAMPS REPAS MIDI
                    repasSeulOuEnGroupe: Yup.string()
                         .label("Type de repas")
                         .when("categorie", {
                              is: CONST_NDF_DETAILS_CATEGORIE_REPAS_MIDI,
                              then: s => s.required("Champ requis"),
                         })
                         .nullable(),
                    invites: Yup.array()
                         .label("Invités")
                         .when("repasSeulOuEnGroupe", {
                              is: "REPAS_MIDI_EN_GROUPE",
                              then: s => s.required("Champ requis"),
                         })
                         .nullable(),

                    // CHAMPS INDEMNITÉ KM
                    moisAnneeKm: Yup.string()
                         .label("Mois")
                         .when("categorie", {
                              is: CONST_NDF_DETAILS_CATEGORIE_KM,
                              then: s => s.required("Sélectionnez le mois"),
                         })
                         .nullable(),
                    joursKm: Yup.array()
                         .label("Jours")
                         .when("categorie", {
                              is: CONST_NDF_DETAILS_CATEGORIE_KM,
                              then: s => s.min(1, "Sélectionnez au moins un jour"),
                         })
                         .nullable(),
                    adresseDepart: Yup.string()
                         .label("Départ")
                         .when("categorie", {
                              is: CONST_NDF_DETAILS_CATEGORIE_KM,
                              then: s => s.required("Champ requis"),
                         })
                         .nullable(),
                    adresseArrive: Yup.string()
                         .label("Arrivée")
                         .when("categorie", {
                              is: CONST_NDF_DETAILS_CATEGORIE_KM,
                              then: s => s.required("Champ requis"),
                         })
                         .nullable(),
                    nbTrajets: Yup.number()
                         .label("Nombre de trajets")
                         .when("categorie", {
                              is: CONST_NDF_DETAILS_CATEGORIE_KM,
                              then: s => s.required("Champ requis"),
                         })
                         .nullable(),
                    distanceTotale: Yup.number()
                         .label("Distance")
                         .when("categorie", {
                              is: CONST_NDF_DETAILS_CATEGORIE_KM,
                              then: s => s.required("Champ requis"),
                         })
                         .nullable(),
                    isAllerRetour: Yup.boolean().notRequired(),
               })
          ),
     })

     const initialValues: INdfDetailEditDepenseGroupedRequestModel = {
          depenses: detail.enfants!.map(item => ({
               id: item.id,
               date: item.date?.format("YYYY-MM-DD") || "",
               categorie: item.categorie as string,
               montantTTC: item.montantTtc || 0,
               description: item.description || "",
               numeroTicket: item.numeroTicket || "",
               repasSeulOuEnGroupe: item.categorie === CONST_NDF_DETAILS_CATEGORIE_REPAS_MIDI ? (item.invites?.length ? "REPAS_MIDI_EN_GROUPE" : "REPAS_MIDI_SEUL") : undefined,
               invites: item.invites || [],
               adresseDepart: item.indemniteKmDetails?.addresseDepart || "",
               adresseArrive: item.indemniteKmDetails?.addresseArrive || "",
               nbTrajets: item.indemniteKmDetails?.nbTrajets || 0,
               isAllerRetour: item.indemniteKmDetails?.isAllerRetour || false,
               distanceTotale: item.indemniteKmDetails?.distanceKmTotal || 0,
               moisAnneeKm: item.date?.format("YYYY-MM") || "",
               joursKm: item.indemniteKmDetails?.joursKm || [],
          })),
     }

     useEffect(() => {
          const kms: Record<number, any> = {}
          detail.enfants!.forEach((item, index) => {
               if (item.categorie === CONST_NDF_DETAILS_CATEGORIE_KM && item.indemniteKmDetails) {
                    kms[index] = {
                         distanceEnKmEntreDepartEtDestination: item.indemniteKmDetails.distanceKmEntreDepartEtArrive,
                         distanceTotal: item.indemniteKmDetails.distanceKmTotal,
                         montantTTC: Number((item.indemniteKmDetails.distanceKmTotal! * CONST_MONTANT_INDEMNITE_PAR_KM).toFixed(2)),
                    }
               }
          })
          setDetailsIndemniteKm(kms)
     }, [detail])

     const handleSubmit = (values: INdfDetailEditDepenseGroupedRequestModel, helpers: FormikHelpers<INdfDetailEditDepenseGroupedRequestModel>) => {
          helpers.setSubmitting(true)
          helpers.setStatus(null)
          ndfDetailEditDepenseGrouped(detail.id as number, values)
               .then(r => {
                    queryClient.setQueryData<IConsultantNdfDetailModel[] | undefined>(["ndfDetails", context.ndfId], (prev: IConsultantNdfDetailModel[] | undefined) => {
                         if (prev) {
                              const index = prev.findIndex(item => item.id === detail.id)
                              if (index >= 0) {
                                   prev[index] = {
                                        ...prev[index],
                                        ...r.data,
                                   }
                              }
                         }
                         context.computeTotalExpenses(prev || [])
                         return prev
                    })

                    helpers.setSubmitting(false)
                    toast.success("Vos modifications ont bien été prises en compte.")
                    context.setCurrentDepenseInEdition(undefined)
               })
               .catch((e: AxiosError) => {
                    const error: IHttpErrorResponseModel = e.response?.data
                    if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                         for (const key in error.errors) helpers.setFieldError(key as any, error.errors[key])
                    }

                    if (error?.code === "REGULARATION_IS_REQUIRED") {
                         helpers.setStatus(
                              <>
                                   <p>{error?.detail}</p>
                                   <b className={"mb-2 fs-5"}>Merci de suivre les étapes suivantes:</b>
                                   <ul>
                                        <li>Accédez à la liste des déclarations.</li>
                                        <li>Demandez la régularisation de la déclaration du mois concerné.</li>
                                        <li>Attendez la validation de votre responsable (utilisez ce temps pour finaliser la déclaration du mois en cours).</li>
                                        <li>Une fois la demande validée, ajoutez votre dépense et vérifiez qu'il n'y a plus de ticket restaurant.</li>
                                        <li>Finaliser et signer votre déclaration.</li>
                                   </ul>
                              </>
                         )
                    } else {
                         helpers.setStatus(error?.detail)
                    }
                    toast.error(error?.detail, { autoClose: false })
                    helpers.setSubmitting(false)
               })
     }

     // --- HANDLERS DOCUMENTS (Mise à jour locale et Loaders) ---
     const onAddFile = (e: React.ChangeEvent<HTMLInputElement>) => {
          const file = e.target.files?.[0]
          if (file) {
               setIsLoadingDocAction(true)
               FileAndDriveHelper.processFiles([file]).then(processedFiles => {
                    ndfDetailAddDocument(detail.id as number, processedFiles[0])
                         .then(r => {
                              setLocalDocuments(prev => [...prev, r.data as any])
                              queryClient.invalidateQueries(["ndfDetails", context.ndfId])
                              toast.success("Le justificatif a bien été ajouté")
                         })
                         .finally(() => {
                              setIsLoadingDocAction(false)
                              if (refAddDocInput.current) refAddDocInput.current.value = ""
                         })
               })
          }
     }

     const onReplaceFile = (e: React.ChangeEvent<HTMLInputElement>) => {
          const file = e.target.files?.[0]
          if (file && docToBeReplaced) {
               setIsLoadingDocAction(true)
               FileAndDriveHelper.processFiles([file]).then(processedFiles => {
                    ndfDetailDocumentReplace(docToBeReplaced.id as number, processedFiles[0])
                         .then(r => {
                              setLocalDocuments(prev =>
                                   prev.map(doc => {
                                        if (doc.id === docToBeReplaced.id) {
                                             return {
                                                  ...doc,
                                                  googleDriveID: r.data.googleDriveID,
                                                  googleDriveMimeType: r.data.googleDriveMimeType,
                                             } as IConsultantNdfDetailDocumentModel
                                        }
                                        return doc
                                   })
                              )
                              queryClient.invalidateQueries(["ndfDetails", context.ndfId])
                              toast.success("Le justificatif a bien été remplacé")
                         })
                         .finally(() => {
                              setIsLoadingDocAction(false)
                              setDocToBeReplaced(undefined)
                              if (refReplaceDocInput.current) refReplaceDocInput.current.value = ""
                         })
               })
          }
     }

     const handleRemoveDoc = (id: number, idx: number) => {
          MySwal.fire({
               title: `Supprimer le justificatif n°${idx}?`,
               icon: "warning",
               showCancelButton: true,
               confirmButtonText: "Oui, supprimer",
               cancelButtonText: "Annuler",
               focusConfirm: true,
          }).then(r => {
               if (r.isConfirmed) {
                    setIsLoadingDocAction(true)
                    ndfDetailDocumentDelete(id)
                         .then(() => {
                              setLocalDocuments(prev => prev.filter(doc => doc.id !== id))
                              queryClient.invalidateQueries(["ndfDetails", context.ndfId])
                              toast.success("Le justificatif a bien été supprimé.")
                         })
                         .finally(() => setIsLoadingDocAction(false))
               }
          })
     }

     const handleViewJustificatif = (reference: string, mimeType: string) => {
          setIsLoadingDocAction(true) // Utilisation du loader pour bloquer les boutons
          toast.info("Chargement du justificatif en cours ...", { autoClose: false, toastId: "loading-doc" })

          axios.post(`${API_URL_ESPACE_AVENTURIER}/download-document`, { reference }, { responseType: "arraybuffer" })
               .then(r => {
                    const url = window.URL.createObjectURL(new Blob([r.data], { type: mimeType }))
                    window.open(url)
                    toast.dismiss("loading-doc")
               })
               .catch((err: AxiosError | any) => {
                    toast.dismiss("loading-doc")
                    toast.error(err.response?.data?.detail || "Erreur lors du téléchargement.", { autoClose: false })
               })
               .finally(() => {
                    setIsLoadingDocAction(false)
               })
     }

     return (
          <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
               {helpers => {
                    async function calculateDistance(index: number, departure?: string, arrival?: string) {
                         if (departure && arrival) {
                              return axios
                                   .post<{ distance: number | null }>(CONST_API_UTILS_GET_GOOGLE_MAPS_DISTANCE_BETWEEN_TWO_POINTS_IN_KM_ENDPOINT, { departure, arrival })
                                   .then(({ data: { distance } }) => {
                                        if (distance) {
                                             setDetailsIndemniteKm(prev => ({
                                                  ...prev,
                                                  [index]: { ...prev[index], distanceEnKmEntreDepartEtDestination: distance },
                                             }))
                                        }
                                        return distance || 0
                                   })
                         }
                         return 0
                    }

                    function syncKM(index: number, dist: number, ar: boolean, nb: number) {
                         const dt = dist > 0 ? Number((dist * (ar ? nb * 2 : nb)).toFixed(2)) : 0
                         const montant = Number((dt * CONST_MONTANT_INDEMNITE_PAR_KM).toFixed(2))
                         setDetailsIndemniteKm(prev => ({
                              ...prev,
                              [index]: { ...prev[index], distanceTotal: dt, montantTTC: montant },
                         }))
                         helpers.setFieldValue(`depenses[${index}].distanceTotale`, dt)
                         helpers.setFieldValue(`depenses[${index}].montantTTC`, montant)
                    }

                    return (
                         <Form noValidate>
                              {helpers.status && (
                                   <MyAlert type={"danger"} classNames={"mb-4"}>
                                        {helpers.status}
                                   </MyAlert>
                              )}
                              <div className="row g-9">
                                   <div className="col-lg-7 col-xl-8 order-2 order-lg-1">
                                        <NdfGroupedDetailsEditSection
                                             helpers={helpers}
                                             validationSchema={validationSchema}
                                             detailsIndemniteKm={detailsIndemniteKm}
                                             setDetailsIndemniteKm={setDetailsIndemniteKm}
                                             calculateDistance={calculateDistance}
                                             syncKM={syncKM}
                                        />
                                        <div className="separator separator-dashed my-6" />
                                        <div className="text-center d-flex justify-content-end">
                                             <button className="btn btn-sm btn-primary" type="submit" disabled={helpers.isSubmitting}>
                                                  {!helpers.isSubmitting ? (
                                                       <>
                                                            Enregistrer <i className="fas fa-check-circle ms-2" />
                                                       </>
                                                  ) : (
                                                       <MySimpleSpinner size="sm" />
                                                  )}
                                             </button>
                                        </div>
                                   </div>

                                   <div className="col-lg-5 col-xl-4 order-1 order-lg-2 mb-8 mb-lg-0">
                                        <div className="position-sticky" style={{ top: "25px" }}>
                                             <NdfJustificatifsEditSection
                                                  documents={localDocuments as any}
                                                  isLoading={isLoadingDocAction}
                                                  onAddClick={() => refAddDocInput.current?.click()}
                                                  onReplaceClick={doc => {
                                                       setDocToBeReplaced(doc as IConsultantNdfDetailDocumentModel)
                                                       refReplaceDocInput.current?.click()
                                                  }}
                                                  onRemoveClick={handleRemoveDoc}
                                                  onViewClick={(doc: any) => handleViewJustificatif(doc.googleDriveID as string, doc.googleDriveMimeType as string)}
                                                  isKm={false} // Pas de required sur justificatifs car géré globalement pour les notes groupées
                                             />
                                        </div>
                                   </div>
                              </div>
                              <input type="file" className="d-none" ref={refAddDocInput} onChange={onAddFile} />
                              <input type="file" className="d-none" ref={refReplaceDocInput} onChange={onReplaceFile} />
                         </Form>
                    )
               }}
          </Formik>
     )
}

export default NdfEditDepenseGrouped
